/**
 * One number, what it counts, and whether it is a problem.
 *
 * Four spellings of this existed: a raw daisyUI `stats` block, a react-daisyui
 * `Stats`, a hand-rolled div, and a bespoke `AdminStatCard`. None of them had
 * room for the footnote that says whether the number is good news.
 *
 * @module components/ui/StatCard
 */

import React, { createContext, type ReactNode, useContext } from "react";

import { HelpTip } from "./HelpTip";
import { Icon } from "./Icon";
import { Eyebrow, Metric } from "./Text";

/**
 * The tones, and the source of the union rather than a copy of it. Spelling
 * the union inline and keeping a parallel array is how `tooltip-${placement}`
 * escaped the safelist: a tone added to one and not the other ships a class
 * Tailwind never builds, with the test still green.
 */
export const STAT_TONES = ["neutral", "success", "warning", "error"] as const;

export type StatTone = (typeof STAT_TONES)[number];

export type StatDensity = "default" | "compact";

/** Lets a `StatGrid` set the density of every card in it at once. */
const StatDensityContext = createContext<StatDensity>("default");

export interface StatCardProps {
  /** `compact` trims the padding and drops the meter, for a screen of many cards. Defaults to its grid's. */
  density?: StatDensity;
  /** Renders the card and its text using inline elements (span) instead of blocks (div, p) */
  inline?: boolean;
  /** What is counted, e.g. "Active tenants". */
  label: string;
  /** The number itself. */
  value: ReactNode;
  /** The number has not arrived: a skeleton stands in for it, not a dash. `"silent"` when the caller's region announces the wait. */
  loading?: boolean | "silent";
  /** Sits beside the value in the smaller size: a unit, or a denominator. */
  unit?: ReactNode;
  /**
   * The whole this value is part of. Draws it as a proportion — `1 / 13` with
   * a share meter — rather than leaving the reader to parse a sentence like
   * "of 13 across the portfolio" into a ratio.
   *
   * **The meter fills toward good.** A filling bar reads as completion and
   * `a / b` reads as "a done, b − a to go", so a card counting a *problem*
   * against a total says the opposite of what it means: "modules without
   * builders, 12 / 13" drew a nearly-full bar over the worst possible state.
   * State the positive quantity and put the number that needs acting on in
   * the `footnote`, or leave `total` off and show the count alone.
   */
  total?: number;
  /** One line under the number, usually qualifying it. */
  footnote?: ReactNode;
  /** Colours the footnote, the meter, and gives the footnote an icon. */
  tone?: StatTone;
  /** Explains the number where the label cannot. */
  help?: string;
  className?: string;
  "data-testid"?: string;
}

const DENSITY: Record<StatDensity, string> = {
  default: "px-5 py-4",
  compact: "px-4 py-3",
};

const TONE: Record<StatTone, { icon: string; className: string }> = {
  neutral: { icon: "info", className: "text-muted" },
  success: { icon: "check", className: "text-success-strong" },
  warning: { icon: "warning", className: "text-warning-strong" },
  error: { icon: "x-circle", className: "text-error-strong" },
};

export const METER: Record<StatTone, string> = {
  neutral: "progress-primary",
  success: "progress-success",
  warning: "progress-warning",
  error: "progress-error",
};

/**
 * The number behind a value, when there is one.
 *
 * @param value - Whatever the caller rendered as the figure.
 * @returns The number it represents, or `null` when it is not one.
 */
function shareOf(value: ReactNode): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return null;
  }
  // Group separators stripped first: a caller formats "1,204" as readily as
  // it passes 1204, and `Number` reads that as NaN, so the card lost its meter
  // while the one beside it showing a bare 9 kept its own.
  const parsed = Number(value.replace(/[,\u202f\u00a0\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @example
 * ```tsx
 * <StatCard label="Modules with builders" value={1} total={13} tone="warning"
 *   footnote="12 modules have no builder" />
 * ```
 */
export function StatCard({
  label,
  value,
  loading = false,
  density = undefined,
  unit = undefined,
  total = undefined,
  footnote = undefined,
  tone = "neutral",
  help = undefined,
  className = "",
  inline = false,
  "data-testid": testId,
}: StatCardProps): React.ReactElement {
  const { icon, className: toneClass } = TONE[tone];
  const inherited = useContext(StatDensityContext);
  const resolvedDensity = density ?? inherited;
  const padding = DENSITY[resolvedDensity];
  // `value` is a ReactNode and callers pass "10" as readily as 10, so gating
  // the meter on `typeof value === "number"` drew the bar for one and silently
  // dropped it for the other.
  const share = shareOf(value);
  const Root = inline ? "span" : "div";
  const Text = inline ? "span" : "p";
  return (
    <Root
      className={`relative block rounded-box border border-base-300 bg-base-200 ${padding} ${className}`.trim()}
      data-testid={testId}
    >
      {/* Not inline: the card is then inside a control, and a button in a
          button is neither valid nor reachable. */}
      {help && !inline && (
        <HelpTip label={`About ${label}`} text={help} className="absolute top-3 right-3" />
      )}
      <Eyebrow as={Text} className={help && !inline ? "block pr-6" : "block"}>
        {label}
      </Eyebrow>
      {/* Separators are text nodes of their own: an accessible name trims each
          element's text and joins inline ones with nothing. */}{" "}
      {loading ? (
        <Text className="mt-1 block">
          {loading === "silent" ? null : (
            <span role="status" className="sr-only">
              {`Loading ${label}`}
            </span>
          )}
          <span aria-hidden="true" className="skeleton block h-8 w-16" />
        </Text>
      ) : (
        <Metric as={Text} className="mt-1 block">
          <span className="tabular-nums">{value}</span>
          {/* A space, not a margin, so the unit is read as a word of its own. */}
          {total != null && " "}
          {total != null && (
            <span className="text-sm font-normal text-muted tabular-nums">/ {total}</span>
          )}
          {unit != null && " "}
          {unit != null && <span className="text-sm font-normal text-muted">{unit}</span>}
        </Metric>
      )}
      {!loading && resolvedDensity === "default" && total != null && share != null && total > 0 && (
        // A real progress element, so the share is reported and not only drawn.
        // Hidden inline: a control's name takes the meter's value as a word, so
        // "3 / 13" would be read as "3 / 13 3".
        <progress
          className={`progress mt-2 h-1 w-full bg-base-300 ${METER[tone]}`}
          value={share}
          max={total}
          aria-label={inline ? undefined : `${label}: ${share} of ${total}`}
          aria-hidden={inline || undefined}
        />
      )}
      {!loading && footnote != null && " "}
      {!loading && footnote != null && (
        <Text className={`mt-2 flex items-start gap-2 text-xs ${toneClass}`}>
          {tone !== "neutral" && <Icon name={icon} aria-hidden className="mt-0.5 shrink-0" />}
          <span>{footnote}</span>
        </Text>
      )}
    </Root>
  );
}

/** Columns at the widest breakpoint; fewer below it, one on a phone. */
export const STAT_GRID_COLUMNS = [2, 3, 4, 6] as const;
export type StatGridColumns = (typeof STAT_GRID_COLUMNS)[number];

/** Written out, not interpolated: Tailwind only generates classes it can read. */
const GRID_CLASS: Record<StatGridColumns, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 xl:grid-cols-3",
  4: "sm:grid-cols-2 xl:grid-cols-4",
  6: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
};

export interface StatGridProps {
  children: ReactNode;
  columns?: StatGridColumns;
  /** Passed to every card in it. */
  density?: StatDensity;
  className?: string;
  "data-testid"?: string;
}

/**
 * A row of `StatCard`s, every card as tall as the tallest: a row of uneven
 * cards reads as a ragged edge, where the empty space reads as alignment.
 */
export function StatGrid({
  children,
  columns = 3,
  density = "default",
  className = "",
  "data-testid": testId,
}: StatGridProps): React.ReactElement {
  return (
    <StatDensityContext.Provider value={density}>
      <div className={`grid gap-3 ${GRID_CLASS[columns]} ${className}`.trim()} data-testid={testId}>
        {children}
      </div>
    </StatDensityContext.Provider>
  );
}
